import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "Home", href: "/", isRoute: true },
  { label: "About", href: "#about", isRoute: false },
  { label: "Events", href: "#events", isRoute: false },
  { label: "Drinks", href: "/menu", isRoute: true },
  { label: "Gallery", href: "/gallery", isRoute: true },
  { label: "Contact", href: "#contact", isRoute: false },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchorClick = (e: React.MouseEvent, href: string) => {
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate("/");
      setTimeout(() => {
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const isActive = (href: string, isRoute: boolean) => {
    if (isRoute) return location.pathname === href;
    return false;
  };

  const linkClass = (href: string, isRoute: boolean) =>
    `font-[Montserrat] text-sm tracking-[0.2em] uppercase transition-colors ${
      isActive(href, isRoute) ? "text-primary" : "text-foreground hover:text-primary"
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-border/50 ${
        scrolled ? "bg-background/95 backdrop-blur-sm shadow-lg" : "bg-background/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-center px-6 py-6">
        {/* Desktop */}
        <ul className="hidden md:flex gap-8">
          {navItems.map((item) => (
            <li key={item.href}>
              {item.isRoute ? (
                <Link
                  to={item.href}
                  className={linkClass(item.href, item.isRoute)}
                  onClick={() => {
                    if (item.href === "/" && location.pathname === "/") {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  href={item.href}
                  className={linkClass(item.href, item.isRoute)}
                  onClick={(e) => handleAnchorClick(e, item.href)}
                >
                  {item.label}
                </a>
              )}
            </li>
          ))}
        </ul>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menü"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileOpen ? (
              <path d="M6 6l12 12M6 18L18 6" />
            ) : (
              <path d="M3 6h18M3 12h18M3 18h18" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-sm border-t border-border">
          <ul className="flex flex-col items-center gap-4 py-6">
            {navItems.map((item) => (
              <li key={item.href}>
                {item.isRoute ? (
                  <Link
                    to={item.href}
                    className={linkClass(item.href, item.isRoute)}
                    onClick={() => {
                      setMobileOpen(false);
                      if (item.href === "/" && location.pathname === "/") {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    className={linkClass(item.href, item.isRoute)}
                    onClick={(e) => {
                      setMobileOpen(false);
                      handleAnchorClick(e, item.href);
                    }}
                  >
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
